/**
 * BatchTranslateDialog.tsx
 * Place at: src/components/cat/BatchTranslateDialog.tsx
 */
import React, { useState, useEffect } from 'react'
import { Zap, X } from 'lucide-react'
import { countSegmentsToTranslate, estimateCharacters, estimateSeconds, type BatchMTProvider, type BatchSegment } from '@/lib/cat/batchMTService'

interface Props {
  isOpen: boolean
  onClose: () => void
  onConfirm: (provider: BatchMTProvider, emptyOnly: boolean) => void
  segments: BatchSegment[]
  defaultProvider?: BatchMTProvider
}

const PROVIDERS = [
  { id: 'deepl' as BatchMTProvider, name: 'DeepL', description: 'Best quality. European languages.', quality: 'Best', qualityColor: 'text-emerald-600' },
  { id: 'google' as BatchMTProvider, name: 'Google Translate', description: 'Fast and reliable. 130+ languages.', quality: 'Good', qualityColor: 'text-blue-600' },
  { id: 'mymemory' as BatchMTProvider, name: 'MyMemory', description: 'Free, unlimited. Lower quality.', quality: 'Basic', qualityColor: 'text-gray-500' },
]

const BatchTranslateDialog: React.FC<Props> = ({ isOpen, onClose, onConfirm, segments, defaultProvider = 'deepl' }) => {
  const [provider, setProvider] = useState<BatchMTProvider>(defaultProvider)
  const [emptyOnly, setEmptyOnly] = useState(true)

  useEffect(() => { if (isOpen) { setProvider(defaultProvider); setEmptyOnly(true) } }, [isOpen])

  if (!isOpen) return null

  const count = countSegmentsToTranslate(segments, emptyOnly)
  const emptyCount = countSegmentsToTranslate(segments, true)
  const allCount = countSegmentsToTranslate(segments, false)
  const chars = estimateCharacters(segments, emptyOnly)
  const secs = estimateSeconds(count, provider)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold-500 rounded-lg"><Zap className="h-5 w-5 text-white" /></div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Batch Translate</h2>
              <p className="text-sm text-gray-500">Pre-fill segments with neural machine translation</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Provider selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">Translation Provider</label>
            <div className="space-y-2">
              {PROVIDERS.map(p => (
                <button key={p.id} onClick={() => setProvider(p.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${provider === p.id ? 'border-gold-500 bg-gold-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${provider === p.id ? 'border-gold-500' : 'border-gray-300'}`}>
                      {provider === p.id && <div className="w-2 h-2 rounded-full bg-gold-500" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800 text-sm">{p.name}</span>
                        <span className={`text-xs font-semibold ${p.qualityColor}`}>{p.quality}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">Segments to Translate</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: true, label: 'Empty only', count: emptyCount, hint: 'Untranslated segments' },
                { value: false, label: 'All segments', count: allCount, hint: 'Overwrites existing' },
              ].map(opt => (
                <button key={String(opt.value)} onClick={() => setEmptyOnly(opt.value)}
                  className={`px-4 py-3 rounded-lg border text-left transition-all ${emptyOnly === opt.value ? 'border-gold-500 bg-gold-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${emptyOnly === opt.value ? 'border-gold-500' : 'border-gray-300'}`}>
                      {emptyOnly === opt.value && <div className="w-2 h-2 rounded-full bg-gold-500" />}
                    </div>
                    <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">{opt.count} segments — {opt.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          {count > 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Summary</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><p className="text-lg font-bold text-slate-800">{count}</p><p className="text-xs text-gray-500">Segments</p></div>
                <div><p className="text-lg font-bold text-slate-800">{chars.toLocaleString()}</p><p className="text-xs text-gray-500">Characters</p></div>
                <div><p className="text-lg font-bold text-slate-800">~{secs}s</p><p className="text-xs text-gray-500">Est. time</p></div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-center text-sm text-gray-400">No segments to translate.</div>
          )}

          {!emptyOnly && (
            <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
              <span>⚠️</span>
              <p><strong>Overwrite mode:</strong> Existing translations will be replaced with MT output.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">Cancel</button>
          <button
            onClick={() => count > 0 && onConfirm(provider, emptyOnly)}
            disabled={count === 0}
            className="flex items-center gap-2 px-5 py-2 bg-gold-500 hover:bg-gold-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <Zap className="h-4 w-4" />
            Translate {count > 0 ? `${count} Segments` : 'Now'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BatchTranslateDialog